import React, { useMemo, useState } from 'react';
import { createCodebookCache } from '../utils/codebookCache';

export interface PhaseTypeOption {
  _id: string;
  name: string;
}

interface PhaseTypeSelectProps {
  value: string | null | undefined;
  onChange: (id: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  searchable?: boolean;
}

// ─── Phase type cache (via shared codebook-cache factory) ───────────────────
// /active is open to the normativ (/os) and RN operators, not only to the Phase Types module;
// invalidateOn refreshes every open dropdown after any write to /api/phase-types.

const phaseTypeCache = createCodebookCache<PhaseTypeOption>({
  endpoint: '/api/phase-types/active',
  invalidateOn: '/api/phase-types',
  params: { sort: 'name', order: 'asc' },
  select: (data) => (Array.isArray(data) ? data : []),
});

/** Call after any successful create/update/delete so next mount fetches fresh data. */
export const invalidatePhaseTypeCache = phaseTypeCache.invalidate;
/** Hook: active phase-type options (stale-while-revalidate, live-updating). */
export const usePhaseTypeOptions = (): PhaseTypeOption[] => phaseTypeCache.useOptions();

const PhaseTypeSelect: React.FC<PhaseTypeSelectProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = '—',
  className = '',
  searchable = true,
}) => {
  const options = usePhaseTypeOptions();
  const [query, setQuery] = useState<string>('');

  const filtered = useMemo(() => {
    if (!searchable || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter(
      (o) => o.name.toLowerCase().includes(q)
    );
  }, [options, query, searchable]);

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {searchable && (
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className="h-9 px-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
        />
      )}
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled}
        className={`h-10 px-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
          disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'
        }`}
      >
        <option value="">{placeholder}</option>
        {filtered.map((p) => (
          <option key={p._id} value={p._id}>
            {p.name}
          </option>
        ))}
      </select>
    </div>
  );
};

export default PhaseTypeSelect;
